
import { defineComponent } from "vue";
import axios from "@/axios-instance";
import Swal from "sweetalert2/dist/sweetalert2.min.js";
import moment from "moment";
import { canViewCandidateDetail } from "@/core/helpers/profileAccess";
import SyncInfoBanner from "@/components/customers/SyncInfoBanner.vue";
import Permission from "@/core/config/Permission"; //gate nút ĐÈ điểm theo Bộ bằng v-permission

export default defineComponent({
  name: "applicant-quick-drawer",
  components: { SyncInfoBanner },
  emits: ["synced"], // khai báo custom event -> listener @synced không rớt thành attribute thừa (root là ElDrawer fragment)
  props: {
    // Trang gọi drawer có cần khối so-lệch 3 bảng + nút đồng bộ không?
    // Danh sách nhập học (TVV): CÓ. Xác nhận thanh toán (kế toán — quan tâm tiền): KHÔNG.
    showSyncCheck: { type: Boolean, default: true },
    // Chế độ TIẾP NHẬN (trang Xác nhận thanh toán): Tiếp nhận & thanh toán lên đầu (chi tiết),
    // phần còn lại gộp thành Khái quát; ẩn chi tiết nhập học rườm rà. Mặc định false = giữ view đầy đủ.
    receptionFocus: { type: Boolean, default: false },
  },
  computed: {
    // Quyền mở chi tiết ứng viên/hồ sơ xét tuyển từ drawer (list nhập học mở cho mọi người,
    // chi tiết thì chỉ phụ trách/trưởng nhóm) — ẩn link nếu không có quyền; BE cũng chặn 403.
    canViewDetail(): boolean {
      const s = (this as any).row?.Student;
      return canViewCandidateDetail(s?.assignee, s?.groupAssignee);
    },
    // Lệch điểm đã có bảng chi tiết riêng bên dưới -> lọc khỏi danh sách gạch đầu dòng (khỏi nói 2 lần)
    otherMismatches(): any[] {
      const res = (this as any).bgdRes;
      if (!res) { return []; }
      const scoreTexts = ((res.scoreDiffs || []) as any[]).map((d: any) => d.text);
      return (res.mismatches || []).filter((m: string) => !scoreTexts.includes(m));
    },
  },
  data() {
    return {
      visible: false,
      row: null as any,
      Permission, //dùng trong template cho v-permission
      bgdRes: null as any, //kết quả đối chiếu bản chụp Bộ (GET applicant/bgdcheck/:id) — null = đang tải/không áp
      bgdApplying: false, //đang ghi điểm theo bản chụp Bộ
      // open theo profileCode: endpoint applicant/profile/:code không trả _receipts -> không rõ Phí giữ suất/Học phí
      noReceiptInfo: false,
      statusTag: {
        completed: { label: "Chờ xác thực", state: "warning" },
        confirmed: { label: "Hoàn tất", state: "success" },
        processing: { label: "Chưa hoàn tất", state: "warning" },
        cancel: { label: "Đã hủy", state: "danger" },
      } as any,
    };
  },
  methods: {
    // rowOrCode: object = row list đã map đủ field (Danh sách nhập học); string = profileCode -> tự fetch
    // overlay: field số tiền ĐÃ TÍNH ở list (total/totalpaid/totalremaining...) — ghi đè sau khi fetch để
    //   khớp số trên bảng (endpoint profile/:code có thể trả totalpaid/totalremaining null -> hiển thị "—" chập chờn).
    async open(rowOrCode: any, overlay: any = null) {
      if (rowOrCode && typeof rowOrCode === "object") {
        this.row = rowOrCode;
        this.noReceiptInfo = false;
        this.visible = true;
        this.loadBgdCheck();
        return;
      }
      try {
        const { data } = await axios.get(`applicant/profile/${rowOrCode}`);
        if (!data || !data.id) {
          Swal.fire({ text: "Không tìm thấy phiếu nhập học cho mã hồ sơ này.", icon: "warning", buttonsStyling: false, confirmButtonText: "Đóng", customClass: { confirmButton: "btn btn-primary" } });
          return;
        }
        // Chuẩn hoá các field mà row list tự map (cùng công thức với admissionProcedureList)
        const fullName = [data.familyName, data.middleName, data.givenName].filter(Boolean).join(" ")
          .replace(/\s+/g, " ").trim().replace(/(^|\s)\S/g, (l: string) => l.toUpperCase());
        const ftr = Array.isArray(data.freeTuitionRegistration) ? data.freeTuitionRegistration : [];
        const scholarshipCode = ftr.find((i: any) => i.registrationType == "scholarships")?.scholarships?.code || "";
        this.row = {
          ...data,
          fullName,
          code: data.admissionRegistration?.profileCode,
          createdAtFormat: this.moment(data.createdAt),
          updatedAtFormat: this.moment(data.updatedAt),
          scholarshipCode: data.status == "cancel" ? "" : scholarshipCode,
          //số tiền list đã tính (đồng nhất bảng) ghi đè cột applicant có thể null
          ...(overlay || {}),
        };
        this.noReceiptInfo = true;
        this.visible = true;
        this.loadBgdCheck();
      } catch (e: any) {
        Swal.fire({ text: e?.response?.data?.message || "Không tải được phiếu nhập học.", icon: "error", buttonsStyling: false, confirmButtonText: "Đóng", customClass: { confirmButton: "btn btn-primary" } });
      }
    },
    // Nạp lại phiếu đang xem theo MHS (sau đồng bộ) — MERGE vào row cũ để giữ các field list đã map
    // (_receipts/isDeposit/adScore/Student...) mà endpoint profile/:code không trả
    async reloadRow() {
      const code = this.row?.code || this.row?.admissionRegistration?.profileCode;
      if (!code) { return; }
      try {
        const { data } = await axios.get(`applicant/profile/${code}`);
        if (!data || !data.id) { return; }
        const fullName = [data.familyName, data.middleName, data.givenName].filter(Boolean).join(" ")
          .replace(/\s+/g, " ").trim().replace(/(^|\s)\S/g, (l: string) => l.toUpperCase());
        this.row = { ...this.row, ...data, fullName };
      } catch (e) { /* giữ bản đang xem nếu nạp lại lỗi */ }
    },
    // Đối chiếu bản chụp Bộ — lỗi/không quyền thì im lặng (khối tự ẩn, không phá drawer)
    async loadBgdCheck() {
      this.bgdRes = null;
      const id = this.row?.id;
      if (!id || !this.showSyncCheck) { return; }
      try {
        const { data } = await axios.get(`applicant/bgdcheck/${id}`);
        if (this.row?.id === id) { this.bgdRes = data; }
      } catch (e) { /* để null — không hiện khối */ }
    },
    // ĐÈ điểm ứng viên theo bản chụp Bộ (PUT applicant/bgdapply/:id) — xác nhận từng ô sẽ đổi trước khi ghi.
    // Chỉ chữa điểm GỐC của ứng viên; điểm xét tuyển đã lưu trong hồ sơ ĐKXT không tự tính lại (nói rõ trong popup).
    applyBgdScores(d: any) {
      const id = this.row?.id;
      if (!id || !d || this.bgdApplying) { return; }
      const esc = (v: any) => String(v ?? "").replace(/</g, "&lt;");
      const rows = (d.cells || []).map((c: any) =>
        `<div class="mb-1"><span class="text-gray-700">${esc(c.mon)}${c.lop ? " " + esc(c.lop) : ""}:</span> ` +
        `<span class="text-decoration-line-through text-muted">${c.he === null ? "trống" : esc(c.he)}</span>` +
        `<i class="bi bi-arrow-right text-gray-500 mx-2"></i><b class="text-primary">${esc(c.bo)}</b></div>`).join("");
      Swal.fire({
        title: "Cập nhật theo điểm Bộ",
        html:
          `<div class="text-start fs-6 mb-3">Ghi <b>${esc(d.label)}</b> của Bộ GD&amp;ĐT đè lên điểm đang lưu của ứng viên?</div>` +
          `<div class="text-start fs-7" style="max-height:220px;overflow-y:auto">${rows}</div>` +
          `<div class="text-start fs-8 text-muted mt-3">Chỉ sửa điểm gốc của ứng viên. Điểm xét tuyển đã lưu trong hồ sơ ĐKXT (và kết quả trúng tuyển) <b>không tự tính lại</b>.</div>`,
        icon: "warning",
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: "Cập nhật",
        cancelButtonText: "Trở lại",
        customClass: { confirmButton: "btn btn-primary order-2", cancelButton: "btn btn-light order-1" },
      }).then(async (result: any) => {
        if (!result.isConfirmed) { return; }
        this.bgdApplying = true;
        try {
          const { data } = await axios.put(`applicant/bgdapply/${id}`, { type: d.type });
          await this.loadBgdCheck(); // đối chiếu lại theo điểm vừa ghi
          Swal.fire({ text: data?.message || "Đã cập nhật theo dữ liệu Bộ.", icon: "success", buttonsStyling: false, confirmButtonText: "Đóng", customClass: { confirmButton: "btn btn-primary" } });
        } catch (e: any) {
          Swal.fire({
            text: e?.response?.data?.errorMessage || e?.response?.data?.message || "Cập nhật thất bại, vui lòng thử lại.",
            icon: "error", buttonsStyling: false, confirmButtonText: "Đóng",
            customClass: { confirmButton: "btn btn-primary" },
          });
        }
        this.bgdApplying = false;
      });
    },
    // Component SyncInfoBanner đồng bộ xong -> nạp lại phiếu đang xem cho khớp bản mới + báo trang cha (list) refresh cờ lệch
    // + đối chiếu Bộ lại (đồng bộ định danh có thể vừa sửa đúng tên/ngày sinh theo Bộ)
    onSynced() {
      this.reloadRow();
      this.loadBgdCheck();
      this.$emit("synced");
    },
    moment(date: any) {
      return date ? moment(date).format("DD/MM/YYYY") : "";
    },
    money(v: any) {
      const n = Number(v);
      if (!v || isNaN(n) || n === 0) return "—";
      return n.toLocaleString("vi-VN") + " đ";
    },
    scoreLabel(v: any) {
      return (v === null || v === undefined || v === "") ? "—" : v;
    },
    programtypeLabel(t: any) {
      const map: any = { TIENG_VIET: "Tiếng Việt", SONG_NGU: "Song ngữ" };
      return map[t] || t || "";
    },
    englishLabel(t: any) {
      const map: any = {
        none: "Không kiểm tra xếp lớp",
        english_thpt: "Điểm Tiếng Anh THPT",
        umt_test: "Thi xếp lớp UMT",
        english_certificate: "Chứng chỉ Tiếng Anh",
      };
      return map[t] || t || "—";
    },
    bhytLabel(t: any) {
      const map: any = { BHYT15: "BHYT 15 tháng", BHYT12: "BHYT 12 tháng", other: "Không đăng ký" };
      return map[t] || t || "—";
    },
    payLabel(code: any) {
      const map: any = { cash: "Tiền mặt", bank_transfers: "Chuyển khoản", atm_card: "ATM/VISA/MASTERCARD" };
      return map[code] || code || "—";
    },
    genderLabel(gender: any) {
      const map: any = { male: "Nam", female: "Nữ", Male: "Nam", Female: "Nữ" };
      return map[gender] || gender || "";
    },
    assigneeLabel(row: any) {
      if (row.Student?.groupAssignee) return row.Student.Group?.groupName || "";
      if (row.Student?.assignee) return row.Student.User?.fullName || "";
      return "";
    },
  },
});
